import {
  getFirestore,
  collection,
  getDocs,
  getDoc,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import _ from "lodash";

export const STANDARD_DECK_TYPE = "standard";
export const TOURNAMENT_DECK_TYPE = "tournament";
export const TOURNAMENT_DEFAULT_DECK_COLOR = "#FFEB3B";

const TOURNAMENT_FULL_SKIP_CARD_NAMES = new Set([
  "technical difficulty",
  "pitfall token",
  "net of binding",
]);

const normalizeCardName = (cardName = "") =>
  String(cardName).trim().toLowerCase().replace(/\s+/g, " ");

export const normalizeDeckType = (deckType) =>
  deckType === TOURNAMENT_DECK_TYPE
    ? TOURNAMENT_DECK_TYPE
    : STANDARD_DECK_TYPE;

export const isTournamentDeck = (deck = {}) =>
  normalizeDeckType(deck?.deckType) === TOURNAMENT_DECK_TYPE;

export const TOURNAMENT_DECK_RULES = [
  {
    key: "fullSkip",
    label: "Full Skip cards",
    max: 2,
    description:
      "Maximum of 2 Full Skip cards (Technical Difficulty, Pitfall Token, Net of Binding, etc.)",
    matches: (card) => TOURNAMENT_FULL_SKIP_CARD_NAMES.has(normalizeCardName(card?.name)),
  },
  {
    key: "stoned",
    label: "Stoned",
    max: 1,
    description: "Maximum of 1 Stoned card",
    matches: (card) => normalizeCardName(card?.name) === "stoned",
  },
  {
    key: "riveredReturn",
    label: "Rivered Return",
    max: 1,
    description: "Maximum of 1 Rivered Return card",
    matches: (card) => normalizeCardName(card?.name) === "rivered return",
  },
  {
    key: "pocketSand",
    label: "Pocket Sand",
    max: 2,
    description: "Maximum of 2 Pocket Sand cards",
    matches: (card) => normalizeCardName(card?.name) === "pocket sand",
  },
  {
    key: "reversalStone",
    label: "Reversal Stone",
    max: 2,
    description: "Maximum of 2 Reversal Stones",
    matches: (card) => normalizeCardName(card?.name) === "reversal stone",
  },
];

const getDeckCardsMatchingRule = (deck = [], rule) =>
  deck.filter((card) => rule.matches(card));

export const getTournamentRuleForCard = (card) =>
  TOURNAMENT_DECK_RULES.find((rule) => rule.matches(card)) || null;

export const getDeckCardLimitState = (
  card,
  deck = [],
  options = {}
) => {
  const normalizedName = normalizeCardName(card?.name);
  const sameCardCount = deck.filter(
    (deckCard) => normalizeCardName(deckCard?.name) === normalizedName
  ).length;
  const cardMax = Number(card?.max);
  const individualLimit = Number.isFinite(cardMax) ? cardMax : Infinity;
  const individualMaxed = sameCardCount >= individualLimit;

  const normalizedDeckType = normalizeDeckType(options?.deckType);
  const tournamentRule =
    normalizedDeckType === TOURNAMENT_DECK_TYPE
      ? getTournamentRuleForCard(card)
      : null;

  if (!tournamentRule) {
    return {
      isMaxed: individualMaxed,
      individualMaxed,
      individualCount: sameCardCount,
      individualLimit,
      ruleMaxed: false,
      ruleCount: null,
      ruleLimit: null,
      ruleLabel: null,
      message: individualMaxed
        ? "You've added the max amount of this card."
        : null,
    };
  }

  const ruleCount = getDeckCardsMatchingRule(deck, tournamentRule).length;
  const ruleMaxed = ruleCount >= tournamentRule.max;

  return {
    isMaxed: individualMaxed || ruleMaxed,
    individualMaxed,
    individualCount: sameCardCount,
    individualLimit,
    ruleMaxed,
    ruleCount,
    ruleLimit: tournamentRule.max,
    ruleLabel: tournamentRule.label,
    message: individualMaxed
      ? "You've added the max amount of this card."
      : ruleMaxed
        ? `Tournament decks can only include ${tournamentRule.max} ${tournamentRule.label}.`
        : null,
  };
};

export const getTournamentDeckViolations = (deck = []) =>
  TOURNAMENT_DECK_RULES.reduce((violations, rule) => {
    const count = getDeckCardsMatchingRule(deck, rule).length;

    if (count > rule.max) {
      violations.push({
        key: rule.key,
        label: rule.label,
        count,
        max: rule.max,
        description: rule.description,
      });
    }

    return violations;
  }, []);

export const getTournamentDeckValidationMessage = (deck = []) => {
  const violations = getTournamentDeckViolations(deck);

  if (!violations.length) return "";

  const [firstViolation] = violations;
  return `${firstViolation.description}. Current count: ${firstViolation.count}.`;
};
// Create Deck Funcitons ===============================================

/**
 * Adds Cards to a deck.
 * @param {array} allCards - all available cards.
 * @param {object} selectedCard - selected card to add to deck.
 * @param {array} newDeck - the current deck.
 * @returns {function} setNewDeck - updated deck.
 */
export const addToDeck = (
  allCards,
  selectedCard,
  newDeck,
  setNewDeck,
  setMessage,
  setError,
  options = {}
) => {
  const countInDeck = newDeck.filter((c) => c.name === selectedCard.name).length;
  const limitState = getDeckCardLimitState(selectedCard, newDeck, options);

  // Count how many of this card the user owns
  // const countInInventory = allCards.filter(
  // 	(c) => c.name === selectedCard.name
  // ).length;
  const countInInventory = selectedCard.amountOwned;

  // Don't add if they reached max allowed or don't have more copies
  if (limitState.isMaxed) {
    setMessage(limitState.message || "You've added the max amount of this card.");
    setError(true);
    return;
  }
  if (countInDeck >= countInInventory) {
    setMessage("Can't add more of this card.");
    setError(true);
    return;
  }
  if (newDeck.length >= 40) {
    setMessage("Your deck is full.");
    setError(true);
    return;
  }

  setNewDeck((prev) => [...prev, selectedCard]);
};

/**
 * Removes a single instance of a card from the deck.
 * @param {object} selectedCard - Card to remove.
 * @param {array} newDeck - Current deck array.
 * @returns {function} setNewDeck - Updated deck.
 */
export const removeFromDeck = (selectedCard, newDeck, setNewDeck) => {
  const index = newDeck.findIndex((c) => c.name === selectedCard.name);

  if (index !== -1) {
    const updatedDeck = [...newDeck];
    updatedDeck.splice(index, 1);
    setNewDeck(updatedDeck);
  } else {
    console.log("Card not found in deck.");
  }
};

/**
 * Saves the deck to Firebase.
 * @param {string} deckName - Name of the deck.
 * @param {object} character - Character associated with the deck.
 * @param {array} deck - Array of cards in the deck.
 * @param {function} resetDeck - Function to reset the deck.
 * @returns {string} message - success message.
 */
export const saveDeckToFirebase = async (
  userData,
  deckName,
  deckColor,
  deck,
  deckId,
  resetDeck,
  options = {}
) => {
  console.log("attempting to save deck");
  //   const auth = getAuth();
  //   console.log("got auth");
  //   const user = auth.currentUser;
  let message = "";

  if (!userData) {
    console.log("User not authenticated or deck is invalid.");
    message = "User not authenticated or deck is invalid.";
    return { success: false, message };
  }

  if (!deck || deck.length !== 40) {
    console.log("Deck is invalid or empty.");
    message = "Can't save this deck. It does not have 40 cards.";
    return { success: false, message };
  }

  if (!deckName || deckName.trim() === "") {
    console.log("Deck name is invalid.");
    message = "Please add a Name to your deck.";
    return { success: false, message };
  }

  const deckType = normalizeDeckType(options?.deckType);

  if (!deckColor) {
    if (deckType === TOURNAMENT_DECK_TYPE) {
      deckColor = TOURNAMENT_DEFAULT_DECK_COLOR;
    } else {
      message = "Please assign a Color to your deck.";
      return { success: false, message };
    }
  }

  const resolvedDeckColor =
    deckType === TOURNAMENT_DECK_TYPE
      ? deckColor || TOURNAMENT_DEFAULT_DECK_COLOR
      : deckColor;

  if (!resolvedDeckColor) {
    message = "Please assign a Color to your deck.";
    return { success: false, message };
  }

  if (deckType === TOURNAMENT_DECK_TYPE) {
    const validationMessage = getTournamentDeckValidationMessage(deck);

    if (validationMessage) {
      return {
        success: false,
        message: validationMessage,
      };
    }
  }

  const db = getFirestore();
  try {
    let targetDeckId = deckId;

    if (!targetDeckId && deckType === TOURNAMENT_DECK_TYPE) {
      const decksSnapshot = await getDocs(collection(db, "users", userData.id, "decks"));
      const existingTournamentDeck = decksSnapshot.docs.find((deckDoc) =>
        isTournamentDeck(deckDoc.data())
      );

      if (existingTournamentDeck) {
        targetDeckId = existingTournamentDeck.id;
      }
    }

    if (targetDeckId) {
      const docRef = doc(db, "users", userData.id, "decks", targetDeckId);
      await updateDoc(docRef, {
        name: deckName,
        // character: character,
        color: resolvedDeckColor,
        cards: deck,
        deckType,
        updatedAt: new Date(),
      });
      console.log("Deck updated in Firestore!");
      message = "Deck updated Successfully!";
      resetDeck();

      return { success: true, message };
    } else {
      await addDoc(collection(db, "users", userData.id, "decks"), {
        name: deckName,
        // character: character,
        color: resolvedDeckColor,
        cards: deck,
        deckType,
        createdAt: new Date(),
      });
      console.log("Deck saved to Firestore!");
      message = "Deck saved Successfully!";
      resetDeck();
      return { success: true, message };
    }
  } catch (error) {
    console.error("Error saving deck:", error);
    message = "Error saving deck, please try again.";
    return { success: false, message };
  }
};

export const deleteDeckFromFirebase = async (
  userData,
  deckId,
  resetDeck,
  options = {}
) => {
  let message = "";
  //   const auth = getAuth();
  //   const user = auth.currentUser;
  if (!userData) {
    console.log("User not authenticated or deck is invalid.");
    message = "User not authenticated or deck is invalid.";
    return { success: false, message };
  }
  const db = getFirestore();
  try {
    const decksRef = collection(db, "users", userData.id, "decks");
    const decksSnapshot = await getDocs(decksRef);

    if (
      normalizeDeckType(options?.deckType) !== TOURNAMENT_DECK_TYPE &&
      decksSnapshot.size <= 1
    ) {
      message = "You can't delete your only deck.";
      return { success: false, message };
    }

    const deckRef = doc(db, "users", userData.id, "decks", deckId);
    await deleteDoc(deckRef);
    console.log("Deck deleted from Firestore!");
    message = "Deck deleted Successfully!";
    if (typeof resetDeck === "function") {
      resetDeck();
    }
    return { success: true, message };
  } catch (error) {
    console.error("Error deleting deck:", error);
    message = "Error deleting deck, please try again.";
    return { success: false, message };
  }
};

export const isFiniteCard = (card) =>
  card?.finite === "TRUE" || card?.finite === "true" || !!card?.finite;

export const getWeakerCounterpart = (finiteCard, infinityCards = []) => {
  if (!isFiniteCard(finiteCard)) return null;
  const baseName = finiteCard?.craftNeeded?.trim();
  if (!baseName) return null;
  return infinityCards.find((c) => (c?.name || "").trim() === baseName) || null;
};

export async function normalizeFiniteAcrossDecks({
  userId,
  finiteName, // e.g., "Blazing Fang Blade"
  totalAllowed, // new amountOwned after decrement
  weakerCard, // full weaker card object
}) {
  const db = getFirestore();
  const decksSnap = await getDocs(collection(db, "users", userId, "decks"));

  // First pass: count total currently allocated across ALL decks (excluding the one we already fixed)
  let allocated = 0;
  const deckDocs = [];
  decksSnap.forEach((d) => {
    const data = d.data();
    const cards = Array.isArray(data.cards) ? data.cards : [];
    const countInThisDeck = cards.filter((c) => c.name === finiteName).length;
    allocated += countInThisDeck;
    deckDocs.push({ id: d.id, cards });
  });

  // If allocation <= allowed, nothing to do (we already fixed current deck).
  if (allocated <= totalAllowed) return;

  // Second pass: trim extras from other decks by replacing with weaker
  let toTrim = allocated - totalAllowed;
  for (const { id, cards } of deckDocs) {
    if (toTrim <= 0) break;
    let replaced = 0;
    const newCards = [];
    for (const c of cards) {
      if (toTrim > 0 && c.name === finiteName) {
        newCards.push(weakerCard);
        toTrim--;
        replaced++;
      } else {
        newCards.push(c);
      }
    }
    if (replaced > 0) {
      await updateDoc(doc(db, "users", userId, "decks", id), {
        cards: newCards,
      });
    }
  }
}

export async function replaceOneInDeck({
  userId,
  deckId,
  finiteName,
  weakerCard,
}) {
  const db = getFirestore();
  const deckRef = doc(db, "users", userId, "decks", deckId);
  const snap = await getDoc(deckRef);
  if (!snap.exists()) return;
  const data = snap.data();
  const cards = Array.isArray(data.cards) ? data.cards : [];

  let replaced = false;
  const newCards = [];
  for (const c of cards) {
    if (!replaced && c.name === finiteName) {
      newCards.push(weakerCard);
      replaced = true;
    } else {
      newCards.push(c);
    }
  }
  if (replaced) {
    await updateDoc(deckRef, { cards: newCards });
  }
}

export const shuffle = (array) => {
  const copy = [...array];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

export const createDeck = () => {
  // Sample deck generator logic
  return Array.from({ length: 60 }, (_, i) => ({
    id: `card-${i + 1}`,
    type: "generic",
    name: `Card ${i + 1}`,
  }));
};

export const parseCardEffect = (card, botPlayer = true) => {
  const rawAffectedAreas = Array.isArray(card?.areaAffected)
    ? card.areaAffected
    : Array.isArray(card?.affectedAreas)
      ? card.affectedAreas
      : [];
  const combinedText = (
    String(card?.name || "") +
    " " +
    String(card?.description || "") +
    " " +
    String(card?.effect || "")
  ).toLowerCase();
  const normalizedCardName = String(card?.name || "").trim().toLowerCase();

  let isAffliction =
    /reduce|decrease|weaken|lower|afflict|damage|poison|lose|remove|takes|slow|unusable|leech|drain|curse/.test(
      combinedText
    );
  let isAid = /restore|increase|heal|boost|raise|enhance|aid|gain|gains|gained/.test(
    combinedText
  );
  const hasTurnLoss = /turn/.test(rawAffectedAreas.join().toLowerCase());

  const amountMatch = card.effect.match(/(\d+)/);
  const amount = amountMatch ? parseInt(amountMatch[1]) : 1;

  const turnsMatch = card.turnsActive?.match(/(\d+)/);
  const duration = turnsMatch ? parseInt(turnsMatch[1]) : 0;

  if (
    ["magic barrier", "cloak of nullification", "the bard's song", "the bards song"].includes(
      normalizedCardName
    )
  ) {
    isAffliction = false;
    isAid = true;
  }
  if (normalizedCardName === "blindfold") {
    isAffliction = true;
    isAid = false;
  }

  // if(botPlayer){
  //   if (card?.specialty) {
  //     switch (card?.name?.toLowerCase()) {
  //       case "cloak of nullification":
  //         isAffliction = false;
  //         isAid = true;
  //         return;
  //       case "lord rune's stone":
  //       case "mirror mist":
  //       case "revenant's echo":
  //       case "rivered return":
  //       case "sleight of hand":
  //       case "soul swap":
  //       case "soulbind slash":
  //       case "stoned":
  //         isAffliction = true;
  //         isAid = false;
  //         return;
  //       default:
  //         return;
  //     }
  //   }
  // }

  return {
    isAffliction,
    imageUrl: card.imageUrl,
    isAid,
    amount: isAffliction ? (hasTurnLoss ? amount : -amount) : amount,
    duration,
    affectedAreas: rawAffectedAreas,
  };
};

export const isSpecialtyLikeCard = (card) => {
  const normalizedCardName = String(card?.name || "").trim().toLowerCase();
  return Boolean(card?.specialty) || ["blindfold"].includes(normalizedCardName);
};

export const resolveCurseAbsorptionHealthAmount = (character, amount) => {
  const numericAmount = Number(amount ?? 0);
  const activeTurns = Number(character?.curseAbsorptionTurns ?? 0);

  if (numericAmount < 0 && activeTurns > 0) {
    return Math.abs(numericAmount);
  }

  return numericAmount;
};

// export function applyTurnBuffs(character) {
//   const stats = ["health", "defense", "attack", "accuracy"];
//   const appliedBuffs = [];

//   stats.forEach((stat) => {
//     const buffs = character.buffs?.[stat];
//     if (!Array.isArray(buffs)) return;

//     const updatedBuffs = [];

//     for (const buff of buffs) {
//       const isInstantAttack =
//         (stat === "attack" || stat === "accuracy") && buff.duration === 0;

//       // Apply only for health and defense
//       if (
//         (stat === "health" || stat === "defense") &&
//         typeof buff.amt === "number"
//       ) {
//         character[stat] += buff.amt;
//         appliedBuffs.push({ stat, amt: buff.amt });
//         console.log(
//           `🟢 ${character.name} gains ${buff.amt} ${stat} (new ${stat}: ${character[stat]})`
//         );
//       }

//       if (!isInstantAttack) {
//         buff.duration -= 1;
//       }

//       if (isInstantAttack || buff.duration > 0) {
//         updatedBuffs.push(buff);
//       }
//     }

//     character.buffs[stat] = updatedBuffs;
//   });

//   return { character, appliedBuffs };
// }

// !V2

// export function applyTurnBuffs(character) {
//   const charCopy = _.cloneDeep(character); // NEW LINE

//   const stats = ["health", "defense", "attack", "accuracy"];
//   const appliedBuffs = [];

//   stats.forEach((stat) => {
//     const buffs = charCopy.buffs?.[stat];
//     if (!Array.isArray(buffs)) return;

//     const updatedBuffs = [];

//     for (const buff of buffs) {
//       const isInstantAttack =
//         (stat === "attack" || stat === "accuracy") && buff.duration === 0;

//       if (
//         (stat === "health" || stat === "defense") &&
//         typeof buff.amt === "number"
//       ) {
//         charCopy[stat] += buff.amt;
//         appliedBuffs.push({ stat, amt: buff.amt });
//       }

//       if (!isInstantAttack) {
//         buff.duration -= 1;
//       }

//       if (isInstantAttack || buff.duration > 0) {
//         updatedBuffs.push(buff);
//       }
//     }

//     charCopy.buffs[stat] = updatedBuffs;
//   });

//   return { character: charCopy, appliedBuffs };
// }

//! V3
export function applyTurnBuffs(character, options = {}) {
  const { deferNegativeHealth = false } = options;

  let charCopy = _.cloneDeep(character);
  charCopy.buffs = charCopy.buffs || {};
  let lastHealthDamageSource = null;

  const stats = ["health", "defense", "attack", "accuracy"];
  let appliedBuffs = [];

  stats.forEach((stat) => {
    const buffs = charCopy.buffs?.[stat];
    if (!Array.isArray(buffs)) return;

    const updatedBuffs = [];

    for (const buff of buffs) {
      const amt = Number(buff?.amt ?? 0);
      const dur = Number(buff?.duration ?? 0);
      const isInstantAttack =
        (stat === "attack" || stat === "accuracy") && dur === 0;

      // ---- HEALTH handling (special) ----
      if (stat === "health" && typeof amt === "number") {
        const resolvedAmt = resolveCurseAbsorptionHealthAmount(charCopy, amt);

        if (resolvedAmt < 0 && deferNegativeHealth) {
          // Defer negative health: do NOT apply and do NOT tick duration.
          // Keep it exactly as-is for the end-of-turn pass.
          updatedBuffs.push(buff);
          continue;
        }

        // Apply (positive health always applies; negative health applies only when not deferring)
        charCopy.health = Number(charCopy.health ?? 0) + resolvedAmt;
        if (charCopy.health < 0) charCopy.health = 0;
        if (resolvedAmt < 0 && buff?.source) {
          lastHealthDamageSource = buff.source;
        }

        appliedBuffs.push({ stat, amt: resolvedAmt });

        // Tick duration for health buffs we actually applied (neg or pos),
        // EXCEPT instant concept doesn't apply to health; just normal tick.
        const newDur = dur - 1;
        if (newDur > 0) {
          updatedBuffs.push({ ...buff, duration: newDur });
        }
        // if <= 0, it expires
        continue;
      }

      // ---- DEFENSE handling ----
      if (stat === "defense" && typeof amt === "number") {
        charCopy.defense = Number(charCopy.defense ?? 0) + amt;
        if (charCopy.defense < 0) charCopy.defense = 0;
        appliedBuffs.push({ stat, amt });

        // normal tick for defense
        const newDur = dur - 1;
        if (newDur > 0) {
          updatedBuffs.push({ ...buff, duration: newDur });
        }
        continue;
      }

      // ---- ATTACK / ACCURACY handling ----
      if ((stat === "attack" || stat === "accuracy") && typeof amt === "number") {
        // We don't mutate base stats here; combat resolution should read from buffs.
        appliedBuffs.push({ stat, amt });

        if (isInstantAttack) {
          // keep instant entries for action-phase consumption
          updatedBuffs.push(buff);
        } else {
          // tick non-instant
          const newDur = dur - 1;
          if (newDur > 0) {
            updatedBuffs.push({ ...buff, duration: newDur });
          }
        }
        continue;
      }

      // Unknown stat type: preserve as-is
      updatedBuffs.push(buff);
    }

    charCopy.buffs[stat] = updatedBuffs;
  });

  // Specialty rules
  if (Number(charCopy.health ?? 0) <= 0) {
    charCopy.isDead = true;
    charCopy.health = 0;
    if (!charCopy.eliminatedBy && lastHealthDamageSource?.id) {
      charCopy.eliminatedBy = {
        name: lastHealthDamageSource?.name || "Affliction",
        id: lastHealthDamageSource?.id,
        character: lastHealthDamageSource?.character || "Affliction",
        cardName: lastHealthDamageSource?.cardName || null,
        eliminatedAt: Date.now(),
      };
    }
  }
  if (Number(charCopy.defense ?? 0) <= 0) {
    charCopy.defense = 0;
    charCopy.stance = "attack";
  }

  return { character: charCopy, appliedBuffs };
}


export const buildBuffMessage = (
  playerName,
  appliedBuffs,
  hasTurnLoss,
  lostMoves
) => {
  const cap = (str) => str.charAt(0).toUpperCase() + str.slice(1);
  let msg = `${playerName} is buffed`;

  if (appliedBuffs.length) {
    const summary = appliedBuffs
      .map((b) => `+${b.amt} ${cap(b.stat)}`)
      .join(" and ");
    msg += ` and received ${summary}.`;
  } else {
    msg = "";
  }

  if (hasTurnLoss && lostMoves > 0) {
    msg += ` However, they lost ${lostMoves > 2 ? 2 : lostMoves} move${
      lostMoves > 1 ? "s" : ""
    } due to a debuff.`;
  }

  return msg;
};
